/**
 * TunnelShell.tsx
 * Main layout container for Tunnel UI experience
 * 
 * V6 FINAL POLISH + BUCKET 4:
 * - CLEAR button has background pill (easier tap target)
 * - Horizontal GameFilterStrip with overflow-x: auto
 * - Clean header layout
 * - BUCKET 4: AllocationSheet integration
 * 
 * BUGFIXES (January 24, 2026):
 * - T1: FLEX filter now excludes QBs (only RB/WR/TE allowed)
 * - T2: Max allocation reserves units for remaining empty slots
 * - T3: Autosave errors shown to user, slot doesn't advance on failure
 */
import React, { useState } from 'react';
import { ChevronLeft, X, AlertCircle, Trophy, PenLine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// BUCKET 5: Data integration hooks and mappers
import { useActiveSlate } from '../../hooks/useActiveSlate';
import { useUserEntry } from '../../hooks/useGMStrategy';
import { useEvents } from '../../hooks/useEvents';
import { useAuth } from '../../context/AuthContext';
import { 
  mapSlatePlayerToTunnel, 
  deriveGamesFromEvents,
  mapEntryToTunnelRoster,
  buildSavePayload,
  findPropById,
  findPlayerByPropId,
  createEmptyRoster,
  type TunnelPlayer,
  type TunnelGame,
  type TunnelRosterSlot,
} from '../../mappers/tunnelDataMapper';

// Components
import { SubtleBackground } from './SubtleBackground';
import { CapRing } from './CapRing';
import { PositionCarousel, Position } from './PositionCarousel';
import { PlayerTileStack } from './PlayerTileStack';
import { PropPicker } from './PropPicker';
import { Player } from './PlayerCard';
import { Prop } from './PropCard';
import { GameFilterStrip } from './GameFilterStrip';
import { AllocationSheet } from './AllocationSheet';  // BUCKET 4

// Helpers
import { countCompletedSlots, calculateTotalUnits } from '../../utils/rosterHelpers';

interface TunnelShellProps {
  poolId: string;
}

const POSITIONS: Position[] = [
  { id: 'QB', label: 'QB' },
  { id: 'RB1', label: 'RB1' },
  { id: 'RB2', label: 'RB2' },
  { id: 'WR1', label: 'WR1' },
  { id: 'WR2', label: 'WR2' },
  { id: 'TE', label: 'TE' },
  { id: 'FLEX', label: 'FLEX' },
];

// T1 FIX: Define valid FLEX positions (QB excluded)
const FLEX_POSITIONS = ['RB', 'WR', 'TE'];

// T2 FIX: Minimum units required per roster slot
const MIN_UNITS_PER_SLOT = 10;

type Screen = 'players' | 'props';

export const TunnelShell: React.FC<TunnelShellProps> = ({ poolId }) => {
  const navigate = useNavigate();
  
  // BUCKET 5: Auth context for user ID
  const { user } = useAuth();
  const userId = String(user?.user_id || user?.id || 'anonymous');
  
  // BUCKET 5: Data hooks - MUST be called before any state that uses their values
  const { players: slatePlayers, loading: slateLoading, error: slateError } = useActiveSlate(poolId);
  const { events, loading: eventsLoading } = useEvents(poolId);
  const { entry, saveEntry } = useUserEntry(poolId, userId);
  
  // BUCKET 5: Transform data to Tunnel schema
  const players: TunnelPlayer[] = React.useMemo(() => 
    (slatePlayers || []).map((p: any) => mapSlatePlayerToTunnel(p, events || [])),
    [slatePlayers, events]
  );
  
  const games: TunnelGame[] = React.useMemo(() => 
    deriveGamesFromEvents(events || []),
    [events]
  );
  
  // Local UI state
  const [currentSlotIndex, setCurrentSlotIndex] = useState(0);
  const [screen, setScreen] = useState<Screen>('players');
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  
  // BUCKET 4: Allocation state
  const [allocationSheetOpen, setAllocationSheetOpen] = useState(false);
  const [selectedProp, setSelectedProp] = useState<Prop | null>(null);
  
  // BUCKET 5: Roster state with entry loading
  const [roster, setRoster] = useState<TunnelRosterSlot[]>(createEmptyRoster());
  
  // T3 FIX: Save error state for inline display
  const [saveError, setSaveError] = useState<string | null>(null);
  
  // T6 FIX: Completion modal state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  
  // BUCKET 5: Load existing entry on mount + enrich from slate data
  React.useEffect(() => {
    if (!entry?.roster) return;

    const loadedRoster = mapEntryToTunnelRoster(entry);

    // Enrich missing display fields from slate data (propId -> player/prop)
    const enriched = loadedRoster.map((slot) => {
      if (!slot.player_prop_id) return slot;

      // If already has display info, keep it
      if (slot.playerName && slot.statType && typeof slot.line === 'number') return slot;

      // Look up from slate
      const player = findPlayerByPropId(slot.player_prop_id, slatePlayers || []);
      const prop = findPropById(slot.player_prop_id, slatePlayers || []);

      return {
        ...slot,
        playerName: slot.playerName ?? player?.name,
        statType: slot.statType ?? (prop?.stat || prop?.statType),
        line: (typeof slot.line === 'number') ? slot.line : prop?.line,
      };
    });

    setRoster(enriched);

    const firstIncomplete = enriched.findIndex(s => !s.player_prop_id);
    if (firstIncomplete >= 0) {
      setCurrentSlotIndex(firstIncomplete);
    }
  }, [entry, slatePlayers]);

  const totalUnits = 100;
  const usedUnits = calculateTotalUnits(roster);
  const remainingUnits = totalUnits - usedUnits;  // BUCKET 4
  const completedSlots = countCompletedSlots(roster);
  const currentPosition = POSITIONS[currentSlotIndex];
  
  // T2 FIX: Calculate max units with reservation for remaining empty slots
  const currentUnits = roster[currentSlotIndex]?.units || 0;
  const availableNow = remainingUnits + currentUnits;
  
  // Count empty slots excluding current slot
  const emptyOtherSlots = roster.filter(
    (s, i) => i !== currentSlotIndex && !s.player_prop_id
  ).length;
  
  // Reserve minimum units for remaining empty slots
  const reserve = MIN_UNITS_PER_SLOT * emptyOtherSlots;
  
  // T2 FIX: Cap to availableNow to prevent accidental >100
  const maxUnitsForCurrentSlot = Math.min(
    availableNow, 
    Math.max(MIN_UNITS_PER_SLOT, availableNow - reserve)
  );
  
  const selectedGameDetails = selectedGame 
    ? games.find((g: TunnelGame) => g.id === selectedGame)
    : null;
  
  const filteredPlayers = selectedGame
    ? players.filter((p: TunnelPlayer) => p.gameId === selectedGame)
    : players;

  // Strip numbers from slot label for position matching (RB1 -> RB, WR2 -> WR)
  const slotPosition = currentPosition.label.replace(/[0-9]/g, '');
  
  // T1 FIX: Separate filtering logic for FLEX vs other positions
  const positionFilteredPlayers = React.useMemo(() => {
    if (currentPosition.label === 'FLEX') {
      // FLEX: Only allow RB, WR, TE (no QBs)
      return filteredPlayers.filter(
        (p: TunnelPlayer) => FLEX_POSITIONS.includes(p.position)
      );
    } else {
      // Non-FLEX: Exact position match
      return filteredPlayers.filter(
        (p: TunnelPlayer) => p.position === slotPosition
      );
    }
  }, [filteredPlayers, currentPosition.label, slotPosition]);
  
  const handleBack = () => {
    if (screen === 'props') {
      setScreen('players');
      setSelectedPlayer(null);
    } else {
      navigate('/');
    }
  };
  
  const handlePositionTap = (index: number) => {
    setCurrentSlotIndex(index);
    setScreen('players');
    setSelectedPlayer(null);
    setSaveError(null); // Clear any previous errors on navigation
  };
  
  const handleSelectPlayer = (player: Player) => {
    setSelectedPlayer(player);
    setScreen('props');
    setSaveError(null); // Clear any previous errors
  };
  
  // BUCKET 4: Opens AllocationSheet
  const handleSelectProp = (prop: Prop) => {
    setSelectedProp(prop);
    setAllocationSheetOpen(true);
    setSaveError(null); // Clear any previous errors
  };
  
  // BUCKET 5: Confirm play, update local state, and save to backend
  // T3 FIX: Improved error handling - show errors to user and don't advance on failure
  const handleConfirmPlay = async (propId: number, side: 'OVER' | 'UNDER', units: number) => {
    // Clear previous errors
    setSaveError(null);
    
    // Get selected data for display
    const selectedPropData = findPropById(propId, slatePlayers || []);
    const selectedPlayerData = findPlayerByPropId(propId, slatePlayers || []);
    
    // Update local state
    const newRoster = [...roster];
    newRoster[currentSlotIndex] = { 
      player_prop_id: propId, 
      side, 
      units,
      playerName: selectedPlayerData?.name,
      statType: selectedPropData?.stat || selectedPropData?.statType,
      line: selectedPropData?.line,
    };
    setRoster(newRoster);
    
    // T3 FIX: Save to backend with proper error handling
    try {
      const payload = buildSavePayload(newRoster, slatePlayers || []);
      const res = await saveEntry(payload.roster);
      
      // T3 FIX: Check for explicit failure response from backend
      // Per architect guidance: Only fail if explicit success: false
      if (res && typeof res === 'object' && 'success' in res && !res.success) {
        const errorMsg = (res as any).error ?? 'Failed to save allocation. Please try again.';
        console.error('Save failed:', errorMsg);
        setSaveError(errorMsg);
        // DO NOT advance to next slot on failure
        return;
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveError('Connection error. Your selection was not saved.');
      // DO NOT advance to next slot on failure
      return;
    }
    
    // Only close sheet and advance if save succeeded
    setAllocationSheetOpen(false);
    setSelectedProp(null);
    
    // Find next incomplete slot
    const nextIncomplete = newRoster.findIndex(
      (slot, i) => i > currentSlotIndex && slot.player_prop_id === null
    );
    const nextFromStart = nextIncomplete === -1
      ? newRoster.findIndex((slot, i) => i !== currentSlotIndex && slot.player_prop_id === null)
      : nextIncomplete;
    
    if (nextFromStart >= 0) {
      setCurrentSlotIndex(nextFromStart);
    } else {
      // T6 FIX: Roster complete - show completion modal
      setShowCompletionModal(true);
      return;
    }
    
    setScreen('players');
    setSelectedPlayer(null);
  };
  
  const handleChangePlayer = () => {
    setScreen('players');
    setSelectedPlayer(null);
  };

  const handleClearFilter = () => {
    setSelectedGame(null);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden">
      <SubtleBackground />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-xl border-b border-slate-800/60 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={handleBack}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-slate-400" />
          </button>
          
          <div className="text-center">
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
              {poolId}
            </p>
            <div className="flex items-center justify-center gap-1.5">
              <span className="text-sm font-bold text-white">
                {screen === 'players' ? `Select ${currentPosition.label}` : 'Set Objective'}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 text-amber-400 rounded border border-amber-500/20 font-mono font-bold">
                {completedSlots}/{POSITIONS.length}
              </span>
            </div>
          </div>
          
          <CapRing used={usedUnits} total={totalUnits} />
        </div>

        <PositionCarousel
          positions={POSITIONS}
          roster={roster}
          activeIndex={currentSlotIndex}
          onPositionTap={handlePositionTap}
        />

        {/* Game Filter Strip - overflow-x: auto for mobile scroll */}
        {screen === 'players' && (
          <div className="pb-3 border-t border-slate-800/50 pt-3">
            <GameFilterStrip
              games={games}
              selectedGame={selectedGame}
              onSelect={setSelectedGame}
            />
          </div>
        )}
      </header>

      {/* T3 FIX: Save error indicator */}
      {saveError && (
        <div className="bg-red-500/20 border-b border-red-500/30 px-4 py-2 flex items-center gap-2">
          <AlertCircle size={16} className="text-red-400" />
          <span className="text-sm text-red-300">{saveError}</span>
          <button 
            onClick={() => setSaveError(null)}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="relative z-10 pt-6 pb-24">
        {/* BUCKET 5: Loading state */}
        {(slateLoading || eventsLoading) && (
          <div className="flex items-center justify-center py-20">
            <div className="text-slate-400">Loading players...</div>
          </div>
        )}
        
        {slateError && (
          <div className="flex items-center justify-center py-20">
            <div className="text-red-400">Error: {slateError}</div>
          </div>
        )}
        
        {!slateLoading && !slateError && screen === 'players' && (
          <>
            {/* Current Pick Card - show what's already selected for this slot */}
            {roster[currentSlotIndex]?.player_prop_id && (
              <div className="px-6 mb-4">
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wide mb-1">
                        Current Pick
                      </div>
                      <div className="text-sm font-bold text-white">
                        {roster[currentSlotIndex].playerName ?? 'Selected Player'}
                      </div>
                      <div className="text-xs text-slate-400">
                        {roster[currentSlotIndex].side} {roster[currentSlotIndex].line} {roster[currentSlotIndex].statType} • {roster[currentSlotIndex].units}u
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        // Clear this slot to re-pick
                        const newRoster = [...roster];
                        newRoster[currentSlotIndex] = { player_prop_id: null, side: null, units: 0 };
                        setRoster(newRoster);
                      }}
                      className="px-3 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-bold"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* My Roster Summary - collapsible view of all picks */}
            {completedSlots > 0 && (
              <div className="px-6 mb-4">
                <details className="bg-slate-900/60 border border-indigo-500/30 rounded-xl">
                  <summary className="px-3 py-2.5 cursor-pointer text-sm text-amber-400 font-bold uppercase tracking-wide flex items-center justify-between">
                    <span>▶ My Roster ({completedSlots}/{POSITIONS.length})</span>
                    <span className="text-[10px] text-slate-400 font-normal normal-case">tap to expand</span>
                  </summary>
                  <div className="px-3 pb-3 space-y-1">
                    {roster.map((slot, idx) => (
                      slot.player_prop_id ? (
                        <div 
                          key={idx} 
                          className={`text-xs py-1.5 px-2 rounded flex justify-between ${idx === currentSlotIndex ? 'bg-indigo-500/20 text-indigo-300' : 'text-slate-300'}`}
                        >
                          <span className="font-bold text-slate-400">{POSITIONS[idx].label}:</span>
                          <span>{slot.playerName ?? '—'} • {slot.side} {slot.line} • {slot.units}u</span>
                        </div>
                      ) : (
                        <div key={idx} className="text-xs py-1.5 px-2 text-slate-600">
                          <span className="font-bold">{POSITIONS[idx].label}:</span> Empty
                        </div>
                      )
                    ))}
                  </div>
                </details>
              </div>
            )}

            {/* Filter indicator with PILL CLEAR BUTTON */}
            {selectedGameDetails && (
              <div className="flex items-center justify-between px-6 mb-4">
                <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">
                  Filtering: {selectedGameDetails.label}
                </span>
                {/* CLEAR button with background pill for easier tap */}
                <button 
                  onClick={handleClearFilter}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-colors"
                >
                  <span className="text-xs font-bold text-slate-400">CLEAR</span>
                  <X size={14} className="text-slate-400" />
                </button>
              </div>
            )}
            
            <PlayerTileStack
              players={(positionFilteredPlayers.length > 0 ? positionFilteredPlayers : filteredPlayers) as Player[]}
              position={currentPosition.label}
              rivalsByPlayer={{}}
              onSelectPlayer={handleSelectPlayer}
            />
          </>
        )}
        
        {!slateLoading && !slateError && screen === 'props' && selectedPlayer && (
          <PropPicker
            player={{
              id: selectedPlayer.id,
              name: selectedPlayer.name,
              team: selectedPlayer.team,
              teamColor: selectedPlayer.teamColor,
              opponent: selectedPlayer.opponent,
            }}
            props={selectedPlayer.props}
            rivalsByProp={{}}
            onSelectProp={handleSelectProp}
            onChangePlayer={handleChangePlayer}
          />
        )}
      </main>

      {/* Bottom Status Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/90 backdrop-blur-xl border-t border-slate-700/50 px-4 py-3 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-1">
              {POSITIONS.slice(0, completedSlots).map((pos) => (
                <div 
                  key={pos.id}
                  className="w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center text-[9px] font-bold border-2 border-slate-900"
                >
                  {pos.label.charAt(0)}
                </div>
              ))}
            </div>
            <span className="text-slate-400 text-sm font-medium">
              {completedSlots} / {POSITIONS.length} plays set
            </span>
          </div>
          <div className="text-right">
            <span className="text-slate-500 text-[11px] uppercase tracking-widest">Impact</span>
            <p className="font-mono text-white font-bold">{usedUnits} / {totalUnits}</p>
          </div>
        </div>
      </div>

{/* T6 FIX: Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl border border-slate-700 p-6 max-w-sm w-full text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Roster Complete!</h2>
            <p className="text-slate-400 text-sm mb-6">All 7 plays are set. What would you like to do?</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 bg-amber-500 text-slate-900 font-bold rounded-xl flex items-center justify-center gap-2"
              >
                <Trophy size={18} />
                Go to War Room
              </button>
              <button
                onClick={() => setShowCompletionModal(false)}
                className="w-full py-3 bg-slate-800 text-slate-300 font-medium rounded-xl flex items-center justify-center gap-2"
              >
                <PenLine size={18} />
                Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BUCKET 4: Allocation Sheet */}
      {/* T2 FIX: Pass calculated maxUnitsForCurrentSlot instead of raw remainingUnits */}
      {selectedPlayer && selectedProp && (
        <AllocationSheet
          isOpen={allocationSheetOpen}
          onClose={() => {
            setAllocationSheetOpen(false);
            setSelectedProp(null);
          }}
          playerName={selectedPlayer.name}
          statType={selectedProp.statType}
          line={selectedProp.line}
          propId={selectedProp.id}
          maxUnits={Math.min(50, maxUnitsForCurrentSlot)}
          onConfirmPlay={handleConfirmPlay}
        />
      )}
    </div>
  );
};

export default TunnelShell;
